import "@/App.css";
import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider } from 'next-themes';
import { Toaster } from 'sonner';
import { trackPageView } from './analytics';
import { useAuthStore } from './store';

// Eager: the landing page (LCP-critical, most-trafficked, the SEO entry point),
// the OAuth callback (matched before routing), and the 404 fallback. Everything
// else is route-split with React.lazy so visitors don't download the workspace
// editor (ReactFlow/Excalidraw/jsPDF/html2canvas), admin, etc. up front. This
// keeps the initial bundle small — the previous single main.js was ~2.4 MB.
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import NotFound from './pages/NotFound';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const Questions = lazy(() => import('./pages/Questions'));
const Workspace = lazy(() => import('./pages/Workspace'));
const Feedback = lazy(() => import('./pages/Feedback'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const Learn = lazy(() => import('./pages/Learn'));
const LearnModule = lazy(() => import('./pages/LearnModule'));
const Quiz = lazy(() => import('./pages/Quiz'));
const QuizSession = lazy(() => import('./pages/QuizSession'));
const Incidents = lazy(() => import('./pages/Incidents'));
const IncidentWorkspace = lazy(() => import('./pages/IncidentWorkspace'));
const IncidentGame = lazy(() => import('./pages/IncidentGame'));
const CompareAttempts = lazy(() => import('./pages/CompareAttempts'));
const PublicProfile = lazy(() => import('./pages/PublicProfile'));
const ProfileSettings = lazy(() => import('./pages/ProfileSettings'));
const SuggestQuestion = lazy(() => import('./pages/SuggestQuestion'));
const PublicQuestion = lazy(() => import('./pages/PublicQuestion'));
const Resources = lazy(() => import('./pages/Resources'));
const Article = lazy(() => import('./pages/Article'));
const SharePage = lazy(() => import('./pages/SharePage'));
const Drills = lazy(() => import('./pages/Drills'));
const NewsletterStatus = lazy(() => import('./pages/NewsletterStatus'));
const TryDrill = lazy(() => import('./pages/TryDrill'));
const ChallengePage = lazy(() => import('./pages/ChallengePage'));
const Daily = lazy(() => import('./pages/Daily'));

// Lightweight full-screen fallback shown while a route chunk loads. Matches the
// spinner style used across the app's own loading states.
function RouteFallback() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary" />
    </div>
  );
}

// Router component to handle auth callback detection
function AppRouter() {
  const location = useLocation();

  // Track page views on every route change
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  // Detect session_id in URL fragment BEFORE any route matching
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/login" element={<Login />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/questions" element={<Questions />} />
      <Route path="/workspace/:attemptId" element={<Workspace />} />
      <Route path="/feedback/:attemptId" element={<Feedback />} />
      <Route path="/attempts/compare" element={<CompareAttempts />} />
      <Route path="/u/:username" element={<PublicProfile />} />
      <Route path="/profile/settings" element={<ProfileSettings />} />
      <Route path="/questions/suggest" element={<SuggestQuestion />} />
      <Route path="/questions/:slug" element={<PublicQuestion />} />
      <Route path="/admin" element={<AdminDashboard />} />

      {/* Resources / Articles (public SEO content hub) */}
      <Route path="/resources" element={<Resources />} />
      <Route path="/resources/:slug" element={<Article />} />

      {/* Weekly teardown newsletter — landing pages for emailed confirm/unsubscribe links */}
      <Route path="/newsletter/confirmed" element={<NewsletterStatus kind="confirmed" />} />
      <Route path="/newsletter/unsubscribed" element={<NewsletterStatus kind="unsubscribed" />} />

      {/* Public shareable scorecard */}
      <Route path="/share/:shareId" element={<SharePage />} />

      {/* Drills — short-form AI-graded practice */}
      <Route path="/drills" element={<Drills />} />

      {/* Public try-a-drill (no signup) + Challenge a friend */}
      <Route path="/try" element={<TryDrill />} />
      <Route path="/c/:challengeId" element={<ChallengePage />} />
      {/* Daily Design — one shared 5-min problem per day (public; play requires sign-in) */}
      <Route path="/daily" element={<Daily />} />

      {/* Learn */}
      <Route path="/learn" element={<Learn />} />
      <Route path="/learn/:moduleId" element={<LearnModule />} />

      {/* Community — hidden from the product surface; routes redirect to dashboard.
          Components/data kept dormant for a possible later relaunch. */}
      <Route path="/community" element={<Navigate to="/dashboard" replace />} />
      <Route path="/community/:questionId" element={<Navigate to="/dashboard" replace />} />

      {/* Quiz */}
      <Route path="/quiz" element={<Quiz />} />
      <Route path="/quiz/session/:quizId" element={<QuizSession />} />

      {/* Incidents */}
      <Route path="/incidents" element={<Incidents />} />
      <Route path="/incidents/:attemptId" element={<IncidentWorkspace />} />
      <Route path="/incident-game" element={<IncidentGame />} />

      {/* Legacy/mistyped URLs → landing page */}
      <Route path="/trail" element={<Navigate to="/" replace />} />
      <Route path="/trial" element={<Navigate to="/" replace />} />

      {/* Catch-all → proper 404 page */}
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  // Logged-out visitors are locked to dark mode. forcedTheme overrides the applied
  // theme WITHOUT clobbering a logged-in user's saved preference — when they sign in,
  // forcedTheme clears and their stored light/dark choice returns.
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const loggedIn = isAuthenticated || (typeof window !== 'undefined' && !!localStorage.getItem('session_token'));

  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="dark"
      storageKey="designlab-theme"
      forcedTheme={loggedIn ? undefined : 'dark'}
    >
      <HelmetProvider>
        <div className="App">
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
          <Toaster
            position="top-right"
            richColors
          />
        </div>
      </HelmetProvider>
    </ThemeProvider>
  );
}

export default App;
